import React from 'react';

import { useScopedI18n } from '@locales/client';
import { Box } from '@/shared/ui/box/Box';
import { Button } from '@/shared/ui/form/button/button';
import BurnWarningIcon from '@/shared/assets/burn-warning.svg';
import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
  BodyTextRegularTertiary,
  BoldHeadingQuaternary,
  BoldHeadingSenary,
} from '@/shared/ui/typography';

import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import * as Dialog from '@radix-ui/react-dialog';

import { useBonusBalance } from '@/shared/providers/notifications-provider/store';

import {
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
  AuthCloseIconImage,
} from '@/shared/ui/alert-dialog/styles';

import {
  BonusDetails,
  BonusDialogContent,
  BurnWarningWrapper,
  DetailItem,
  DetailItems,
} from '../../styles';

import {
  BonusItemBurns,
  IconSpacingBox,
  CenteredTitleBox,
  CenteredDescBox,
  ButtonsRow,
} from './styles';
import { createDialogCloseOnlyHandler } from '@/shared/libs/dialog-open-change';
import { useFormatAmount } from '@/shared/hooks/useFormatAmount';

export const BonusBurnModal = ({
  visible,
  onClose,
  bonus,
  progressWager,
  onDeactivate,
}) => {
  const t = useScopedI18n('bonus');
  const bonusBalance = useBonusBalance();
  const formatAmount = useFormatAmount({ mode: 'plain' });

  if (!bonus) return null;
  
  return (
    <Dialog.Root open={visible} onOpenChange={createDialogCloseOnlyHandler(onClose)}>
      <Dialog.Portal>
        <StyledAlertDialogOverlay onClick={onClose} />
        <StyledAlertDialogContent aria-describedby="" data-item="bonus_burn_confirm">
          <BonusDialogContent>
            <BonusItemBurns>
              <BonusDetails style={{ marginBottom: 0 }}>
                <VisuallyHidden>
                  <Dialog.Title>bonus delete</Dialog.Title>
                </VisuallyHidden>
                <Box onClick={onClose}>
                  <AuthCloseIconImage
                    width={32}
                    height={32}
                    src="/images/icons/close-dialog.svg"
                    alt="Close dialog"
                  />
                </Box>
                <IconSpacingBox>
                  <BurnWarningWrapper>
                    <BurnWarningIcon />
                  </BurnWarningWrapper>
                </IconSpacingBox>
                <CenteredTitleBox>
                  <BoldHeadingQuaternary>
                    {t('burn_modal.title')}
                  </BoldHeadingQuaternary>
                </CenteredTitleBox>
                <CenteredDescBox>
                  <BodyTextRegularSecondary color="#AFAFAF">
                    {t('burn_modal.desc2')}:
                  </BodyTextRegularSecondary>
                </CenteredDescBox>
                <Box></Box>
                <DetailItems>
                  <DetailItem>
                    <BodyTextRegularTertiary color="#AEB8D1">
                      {t('burn_modal.bonus_balance')}
                    </BodyTextRegularTertiary>
                    <BoldHeadingSenary>
                      {formatAmount(bonusBalance)}
                    </BoldHeadingSenary>
                  </DetailItem>
                  <DetailItem>
                    <BodyTextRegularTertiary color="#AEB8D1">
                      {t('burn_modal.wager_progress')}
                    </BodyTextRegularTertiary>
                    <BoldHeadingSenary>
                      {Number(progressWager).toFixed(2)}
                    </BoldHeadingSenary>
                  </DetailItem>
                </DetailItems>
                <ButtonsRow>
                  <Button
                    type="button"
                    onClick={() => onDeactivate(bonus)}
                    fill
                    variant="danger"
                    data-analytics-id="bonus_burn_confirm"
                  >
                    <Box>
                      <BodyTextBoldSecondary>
                        {t('card.button_burn')}
                      </BodyTextBoldSecondary>
                    </Box>
                  </Button>
                  <Button type="button" onClick={onClose} fill variant="gray">
                    <Box>
                      <BodyTextBoldSecondary>
                        {t('burn_modal.button_close')}
                      </BodyTextBoldSecondary>
                    </Box>
                  </Button>
                </ButtonsRow>
              </BonusDetails>
            </BonusItemBurns>
          </BonusDialogContent>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
